import React, { useState } from 'react';
import { Switch, FormControlLabel, IconButton, Tooltip, Menu, MenuItem } from '@mui/material';
import type { MouseEvent } from 'react';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  PlayArrow as PlayIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterListIcon,
  Route as RouteIcon,
} from '@mui/icons-material';
import { ShiftTemplate, TemplateStatus, RecurrenceType } from '../types';
import { formatDate, formatTime } from '../utils';
import { Badge } from '../../../pages/ShiftManagement/components/ui/Badge';

const shouldShowTooltip = (text: string | undefined | null): boolean => {
  return text ? text.trim().length > 0 : false;
};

interface TemplateCardProps {
  template: ShiftTemplate;
  onViewTemplate: (templateId: string) => void;
  onEditTemplate: (templateId: string) => void;
  onDeleteTemplate: (templateId: string) => void;
  onToggleTemplate: (templateId: string) => void;
  onGenerateShifts: (templateId: string) => void;
  getCityName: (cityId?: string | number) => string;
  getNeighborhoodName: (neighborhoodId?: string) => string;
  getRecurrenceLabel: (type: RecurrenceType, template: ShiftTemplate) => string;
  getAssignedDriversCount: (template: ShiftTemplate) => number;
  getStatusBadgeVariant: (status: TemplateStatus) => 'success' | 'secondary';
  getRecurrenceBadgeVariant: (type: RecurrenceType) => string;
}

export const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onViewTemplate,
  onEditTemplate,
  onDeleteTemplate,
  onToggleTemplate,
  onGenerateShifts,
  getCityName,
  getNeighborhoodName,
  getRecurrenceLabel,
  getAssignedDriversCount,
  getStatusBadgeVariant,
  getRecurrenceBadgeVariant,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const assignedCount = getAssignedDriversCount(template);
  const isFullyAssigned = assignedCount >= template.requiredDrivers;
  const isNearlyAssigned = assignedCount >= template.requiredDrivers * 0.8;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget as HTMLElement);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    handleClose();
    onViewTemplate(template.id);
  };

  const handleEdit = () => {
    handleClose();
    onEditTemplate(template.id);
  };

  const handleDelete = () => {
    handleClose();
    onDeleteTemplate(template.id);
  };

  const handleGenerate = () => {
    handleClose();
    onGenerateShifts(template.id);
  };

  return (
    <div className="group rounded-lg border border-gray-300 shadow-[0_1px_4px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.1)] transition-all duration-300 relative overflow-hidden">
      <div className="p-5">
        {/* Shift Schedule Name */}
        <div className="flex items-start justify-between mb-1.5">
          {shouldShowTooltip(template.name) ? (
            <Tooltip
              title={template.name}
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'white',
                    color: 'rgba(0, 0, 0, 0.87)',
                    fontSize: '0.75rem',
                    maxWidth: '200px',
                    padding: '8px 12px',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  }
                },
                arrow: {
                  sx: {
                    color: 'white',
                  }
                }
              }}
            >
              <h3 className="text-sm font-bold text-gray-900 truncate leading-tight flex-1 pr-2 cursor-help min-w-0">
                <span className="text-xs font-mono text-gray-500 font-normal">ID: {template.id}</span>
                <span className="mx-2 text-orange-500">|</span>
                {template.name}
              </h3>
            </Tooltip>
          ) : (
            <h3 className="text-sm font-bold text-gray-900 truncate leading-tight flex-1 pr-2 min-w-0">
              <span className="text-xs font-mono text-gray-500 font-normal">ID: {template.id}</span>
              <span className="mx-2 text-orange-500">|</span>
              {template.name}
            </h3>
          )}
          {/* Actions Menu */}
          <IconButton
            size="small"
            onClick={handleClick}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            title="More options"
            sx={{
              padding: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onClick={(e) => e.stopPropagation()}
            PaperProps={{
              sx: {
                borderRadius: '10px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                minWidth: '150px',
                mt: 0.5,
                border: '1px solid rgba(0, 0, 0, 0.06)'
              }
            }}
          >
            <MenuItem
              onClick={handleView}
              sx={{
                fontSize: '13px',
                py: 1.25,
                '&:hover': {
                  backgroundColor: 'rgba(59, 130, 246, 0.06)',
                }
              }}
            >
              <VisibilityIcon fontSize="small" sx={{ mr: 1.5, color: '#3B82F6', fontSize: '16px' }} />
              View Details
            </MenuItem>
            {/* Temporarily hidden: Generate Shifts */}
            {/* <MenuItem 
              onClick={handleGenerate}
              disabled={template.status === 'inactive'}
              sx={{
                fontSize: '13px',
                py: 1.25,
                '&:hover': {
                  backgroundColor: 'rgba(139, 92, 246, 0.06)',
                },
                '&.Mui-disabled': {
                  opacity: 0.5,
                }
              }}
            >
              <PlayIcon fontSize="small" sx={{ mr: 1.5, color: template.status === 'inactive' ? '#9CA3AF' : '#8B5CF6', fontSize: '16px' }} />
              Generate Shifts
            </MenuItem> */}
            <MenuItem
              onClick={handleEdit}
              sx={{
                fontSize: '13px',
                py: 1.25,
                '&:hover': {
                  backgroundColor: 'rgba(59, 130, 246, 0.06)',
                }
              }}
            >
              <EditIcon fontSize="small" sx={{ mr: 1.5, color: '#3B82F6', fontSize: '16px' }} />
              Edit
            </MenuItem>
            <MenuItem
              onClick={handleDelete}
              sx={{
                fontSize: '13px',
                py: 1.25,
                color: '#EF4444',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.06)',
                }
              }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1.5, fontSize: '16px' }} />
              Delete
            </MenuItem>
          </Menu>
        </div>

        {/* Status and Priority */}
        <div className="flex items-center gap-2 mb-2">
          <Badge variant={getStatusBadgeVariant(template.status)}>
            {template.status === 'active' ? 'Active' : 'Inactive'}
          </Badge>
          <span className="text-xs text-gray-600 font-medium">Priority {template.priority || 0}</span>
        </div>

        {/* Content Section */}
        <div className="space-y-2">
          {/* Recurrence Configuration Section */}
          <div className="bg-blue-50/40 rounded-md p-2 border border-blue-100/60">
            <div className="flex items-center gap-1.5 mb-1.5">
              <RouteIcon style={{ fontSize: '14px', color: '#3B82F6' }} />
              <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wide">
                Recurrence Configuration
              </span>
            </div>
            <div className="text-xs">
              <div className="flex items-center gap-3 flex-wrap mb-1.5">
                <span className="font-semibold text-gray-700">Type</span>
                <Badge variant={getRecurrenceBadgeVariant(template.recurrenceType) as any} className="text-[10px] font-semibold">
                  {getRecurrenceLabel(template.recurrenceType, template)}
                </Badge>
              </div>
              {template.recurrenceType === 'specific_date' && template.specificDate && (
                <div className="flex items-center gap-1.5 mt-1">
                  <ScheduleIcon style={{ fontSize: '12px', color: '#6B7280' }} />
                  <span className="font-semibold text-gray-700">Date:</span>
                  <span className="text-gray-800 font-medium">{formatDate(template.specificDate)}</span>
                </div>
              )}
              {template.recurrenceType === 'weekly' && template.daysOfWeekNames && template.daysOfWeekNames.length > 0 && (
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <ScheduleIcon style={{ fontSize: '12px', color: '#6B7280' }} />
                  <span className="font-semibold text-gray-700">Days:</span>
                  <span className="text-gray-800 font-medium">{template.daysOfWeekNames.join(', ')}</span>
                </div>
              )}
              {template.recurrenceType === 'custom_range' && (template.validFrom || template.validUntil) && (
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <ScheduleIcon style={{ fontSize: '12px', color: '#6B7280' }} />
                  <span className="font-semibold text-gray-700">Range:</span>
                  <span className="text-gray-800 font-medium">
                    {template.validFrom && template.validUntil
                      ? `${formatDate(template.validFrom)} to ${formatDate(template.validUntil)}`
                      : template.validFrom
                        ? `From: ${formatDate(template.validFrom)}`
                        : `Until: ${formatDate(template.validUntil)}`
                    }
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Template Details Section */}
          <div className="bg-purple-50/40 rounded-md p-2 border border-purple-100/60">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <FilterListIcon style={{ fontSize: '14px', color: '#9333EA' }} />
                <span className="text-[10px] font-bold text-purple-700 uppercase tracking-wide">Shift Schedule Details</span>
              </div>
            </div>

            <div className="mt-1.5 space-y-1.5">
              <div className="flex flex-wrap items-center gap-1.5 text-xs bg-white px-2 py-1.5 rounded-md shadow-sm border border-gray-100">
                <ScheduleIcon style={{ fontSize: '12px', color: '#6B7280' }} />
                <span className="font-bold text-gray-900">Time:</span>
                <span className="font-semibold text-primaryColor">{formatTime(template.startTime)} - {formatTime(template.endTime)}</span>
              </div>

              <div className="flex flex-wrap items-center gap-1.5 text-xs bg-white px-2 py-1.5 rounded-md shadow-sm border border-gray-100">
                <PeopleIcon style={{ fontSize: '12px', color: '#6B7280' }} />
                <span className="font-bold text-gray-900">Drivers:</span>
                <span className={`font-semibold ${isFullyAssigned
                    ? 'text-green-700'
                    : isNearlyAssigned
                      ? 'text-amber-700'
                      : 'text-red-700'
                  }`}>
                  {assignedCount} / {template.requiredDrivers}
                </span>
              </div>

              {template.cityId && (
                <div className="flex flex-wrap items-center gap-1.5 text-xs bg-white px-2 py-1.5 rounded-md shadow-sm border border-gray-100">
                  <LocationIcon style={{ fontSize: '12px', color: '#6B7280' }} />
                  <span className="font-bold text-gray-900">City:</span>
                  <span className="font-semibold text-primaryColor">{getCityName(template.cityId)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Toggle Switch */}
        <div className="pt-3 border-t border-gray-200 mt-3">
          <FormControlLabel
            control={
              <Switch
                checked={template.status === 'active'}
                onChange={() => onToggleTemplate(template.id)}
                color="primary"
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#10B981',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#10B981',
                  },
                }}
              />
            }
            label={
              <span className="text-xs font-medium text-gray-700">
                {template.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            }
            sx={{
              margin: 0,
              '& .MuiFormControlLabel-label': {
                marginLeft: '8px',
              },
            }}
          />
        </div>
      </div>
    </div>
  );
};

